import {
  Path,
  useForm,
  FieldErrors,
  SubmitHandler,
  SubmitErrorHandler,
  useWatch,
  Mode,
  FieldError,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodSchema, TypeOf } from 'zod';
import { ClientFormData } from '../pages/CreateNewClient/CreateNewClientUtils';
import { useBackendValidation } from './useBackendValidation';
import {
  errorsProps,
  ValidationErrorTypes,
  warningsProps,
} from '../components/SummaryErrors/SummaryErrors';
import { mergeValidationErrors } from '../utils/mergeValidationErrors';
import { removeOtherErrorsIfThereIsFieldRequiredError } from '../utils/filterFieldErrors';
import { useCallback, useEffect, useRef } from 'react';

export const formatFieldError = (
  fieldPath: string,
  error: any,
  fieldLabels: Record<string, string>
): errorsProps | null => {
  if (!error)
    return {
      fieldName: fieldPath,
      fieldErrors: [],
      fieldLabel: fieldLabels[fieldPath] || fieldPath,
      type: ValidationErrorTypes.Frontend,
    };

  const errorMessages = new Set<string>();

  if (typeof error.message === 'string') {
    errorMessages.add(error.message);
  }

  if (error.types) {
    Object.values(error.types).forEach((msg) => {
      if (Array.isArray(msg)) {
        msg.forEach((m) => errorMessages.add(m));
      } else {
        errorMessages.add(msg as string);
      }
    });
  }

  if (Array.isArray(error)) {
    error.forEach((e) => e.message && errorMessages.add(e.message));
  }

  if (errorMessages.size === 0) {
    return null;
  }

  return {
    fieldName: fieldPath,
    fieldLabel: fieldLabels[fieldPath] || fieldPath,
    fieldErrors: Array.from(errorMessages),
    type: ValidationErrorTypes.Frontend,
  };
};

export const flattenNestedEntries = <T>(
  obj: Record<string, Record<string, T>>
): [string, T][] => Object.values(obj).flatMap(Object.entries);

const isSectionErrors = (
  error: unknown
): error is Record<string, FieldError> => {
  if (typeof error !== 'object' || error === null || Array.isArray(error)) {
    return false;
  }
  const candidate = error as Partial<FieldError>;
  return (
    typeof candidate.type !== 'string' &&
    typeof candidate.message !== 'string' &&
    candidate.types === undefined
  );
};

const flattenFieldErrors = (
  fieldPath: string,
  error: unknown,
  fieldLabels: Record<string, string>
): errorsProps[] => {
  if (isSectionErrors(error)) {
    return Object.entries(error).flatMap(([nestedFieldPath, nestedError]) =>
      flattenFieldErrors(
        `${fieldPath}.${nestedFieldPath}`,
        nestedError,
        fieldLabels
      )
    );
  }
  const formattedError = formatFieldError(fieldPath, error, fieldLabels);
  return formattedError ? [formattedError] : [];
};

export const useCustomForm = <T extends ZodSchema>(
  schema: T,
  fieldLabels: Record<string, string>,
  options: {
    defaultValues?: z.infer<T>;
    validationMode?: Mode;
    getFrontendWarnings?: (
      data: z.infer<T>,
      fieldsToValidate: Record<string, boolean>
    ) => warningsProps[];
    backendValidation?: {
      validate?: (formData: z.infer<T>) => Promise<unknown>;
      validationErrors?: errorsProps[];
      isValid?: boolean;
      isValidating?: boolean;
      affectedFieldsToFormFieldsMap?: Record<string, string>;
      initialBackendErrors?: errorsProps[];
      formIsDirty?: boolean;
    };
  }
) => {
  type FormData = z.infer<T>;

  const { defaultValues, backendValidation, validationMode = 'all' } = options;

  const {
    control,
    handleSubmit,
    formState: {
      errors,
      isDirty,
      isValid,
      touchedFields,
      dirtyFields,
      defaultValues: formDefaultValues,
    },
    trigger,
    setFocus,
    getValues,
    watch,
    setError,
    setValue,
    reset,
    subscribe,
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: validationMode,
    criteriaMode: 'all',
    defaultValues: defaultValues as FormData,
  });
  const formData = useWatch({ control });
  const currentFormDataSnapshot = JSON.stringify(formData);
  const validatedFormDataSnapshotRef = useRef<string | null>(null);
  const formErrors: FieldErrors<FormData> = (() => {
    const filteredErrors: Record<string, unknown> = {};

    Object.entries(errors).forEach(([key, error]) => {
      if (error?.type === 'backend') {
        return;
      }

      filteredErrors[key] = removeOtherErrorsIfThereIsFieldRequiredError(
        error as FieldError
      );
    });

    return filteredErrors as FieldErrors<FormData>;
  })();
  const backendValidationResult = backendValidation
    ? useBackendValidation({
        validate: backendValidation.validate,
        formDataIsDirty:
          'formIsDirty' in backendValidation &&
          backendValidation.formIsDirty !== undefined
            ? backendValidation.formIsDirty
            : isDirty,
        frontendValidationPassed: schema.safeParse(formData).success,
        fieldLabels,
        formData: formData,
        initialErrors:
          'initialBackendErrors' in backendValidation
            ? backendValidation.initialBackendErrors
            : undefined,
        onValidationComplete: (validatedData: FormData) => {
          validatedFormDataSnapshotRef.current = JSON.stringify(validatedData);
        },
      })
    : {
        validationErrors: [],
        isValid: true,
        isValidating: false,
        hasValidatedCurrentForm: true,
      };
  const backendValidationErrors =
    backendValidation && typeof backendValidation.validate === 'function'
      ? (
          'formIsDirty' in backendValidation &&
          backendValidation.formIsDirty !== undefined
            ? backendValidation.formIsDirty
            : isDirty
        )
        ? backendValidationResult.validationErrors
        : (backendValidation.initialBackendErrors ?? [])
      : backendValidation?.validationErrors;

  const isBackendValid =
    backendValidation && typeof backendValidation.validate === 'function'
      ? backendValidationResult.isValid
      : backendValidation?.isValid;
  const isBackendValidating =
    backendValidation && typeof backendValidation.validate === 'function'
      ? backendValidationResult.isValidating
      : backendValidation?.isValidating;
  const hasBackendValidationSettled =
    backendValidation && typeof backendValidation.validate === 'function'
      ? backendValidationResult.hasValidatedCurrentForm
      : true;
  const affectedFieldsToFormFieldsMap =
    options.backendValidation?.affectedFieldsToFormFieldsMap || {};
  const isBackendValidationCurrent =
    !backendValidation ||
    typeof backendValidation.validate !== 'function' ||
    (validatedFormDataSnapshotRef.current !== null &&
      currentFormDataSnapshot === validatedFormDataSnapshotRef.current);

  const frontendFormattedErrors = Object.entries(
    formErrors as FieldErrors<FormData>
  ).flatMap(([fieldPath, error]) =>
    flattenFieldErrors(fieldPath, error, fieldLabels)
  );
  const usingInitialBackendErrors =
    backendValidation &&
    typeof backendValidation.validate === 'function' &&
    !isDirty &&
    Object.keys(affectedFieldsToFormFieldsMap).length > 0;

  const backendFormattedErrors = (backendValidationErrors || []).map(
    (backendError) => {
      const remappedFieldName =
        affectedFieldsToFormFieldsMap[backendError.fieldName] ||
        backendError.fieldName;
      return {
        ...backendError,
        fieldName: remappedFieldName,
        fieldLabel: fieldLabels[remappedFieldName] || backendError.fieldLabel,
        affectedFields: backendError.affectedFields
          ?.filter(
            (af) =>
              !usingInitialBackendErrors ||
              af.fieldName in affectedFieldsToFormFieldsMap
          )
          .map((af) => {
            const remappedAFName =
              affectedFieldsToFormFieldsMap[af.fieldName] || af.fieldName;
            return {
              ...af,
              fieldName: remappedAFName,
              fieldLabel: fieldLabels[remappedAFName] || af.fieldLabel,
            };
          }),
      };
    }
  );
  const formattedErrors: errorsProps[] = mergeValidationErrors([
    ...frontendFormattedErrors,
    ...backendFormattedErrors,
    ...Object.entries(errors)
      .filter(
        ([fieldName, fieldError]) =>
          (fieldError as FieldError)?.type === 'backend' &&
          !backendFormattedErrors.some((e) => e.fieldName === fieldName)
      )
      .map(([fieldName, fieldError]) => ({
        fieldName,
        fieldLabel: fieldLabels[fieldName] || fieldName,
        fieldErrors: [(fieldError as FieldError).message || ''],
        type: ValidationErrorTypes.Backend,
        affectedFields: [],
      })),
  ]);

  const isBackendValidationActive =
    backendValidation && typeof backendValidation.validate === 'function';

  useEffect(() => {
    if (
      !hasBackendValidationSettled ||
      !isBackendValidationCurrent ||
      !isBackendValidationActive
    ) {
      return;
    }

    const backendFieldNames = new Set(
      backendFormattedErrors.map((e) => e.fieldName)
    );
    Object.entries(errors).forEach(([fieldName, fieldError]) => {
      if (
        (fieldError as FieldError)?.type === 'backend' &&
        !backendFieldNames.has(fieldName)
      ) {
        clearErrors(fieldName as Path<FormData>);
      }
    });
  }, [
    hasBackendValidationSettled,
    isBackendValidationCurrent,
    isBackendValidationActive,
    JSON.stringify(backendFormattedErrors.map((e) => e.fieldName).sort()),
  ]);

  const warningsFromBackend =
    'warnings' in backendValidationResult
      ? backendValidationResult.warnings
      : [];
  const fieldsToValidate = {
    ...(touchedFields as Record<string, boolean>),
    ...(dirtyFields as Record<string, boolean>),
  };
  const warningsFromFrontend =
    options.getFrontendWarnings?.(formData, fieldsToValidate) || [];
  const warnings = [...warningsFromBackend, ...warningsFromFrontend];

  const handleErrorClick = (fieldPath: Path<TypeOf<T>>) => {
    setFocus(fieldPath);
  };

  const handleBlur = async () => {
    await trigger();
  };

  const triggerField = async (fieldName: keyof ClientFormData) => {
    await trigger(fieldName as Path<TypeOf<T>>);
  };

  const isFormValid =
    isBackendValid === undefined
      ? isValid
      : isValid && isBackendValid && !isBackendValidating;
  const canSubmit =
    hasBackendValidationSettled && isBackendValidationCurrent && isFormValid;

  const triggerWrapper = useCallback(
    async (
      name?: Parameters<typeof trigger>[0],
      options?: Parameters<typeof trigger>[1] & {
        triggerBackendValidation?: boolean;
        data?: Partial<FormData>;
      }
    ) => {
      const frontendValid = await trigger(name, options);
      if (!frontendValid) {
        return false;
      }

      if (
        backendValidationResult &&
        'triggerBackendValidation' in backendValidationResult &&
        options?.triggerBackendValidation
      ) {
        const data = options.data ? (options.data as FormData) : formData;
        await backendValidationResult.triggerBackendValidation(data);
      } else if (backendValidation && 'isValid' in backendValidation) {
        const backendValid = backendValidation.isValid;
        return Boolean(backendValid);
      }
      return true;
    },
    [
      trigger,
      JSON.stringify(formData),
      JSON.stringify(backendValidationResult),
      JSON.stringify(backendValidation),
    ]
  );

  const wrappedHandleSubmit = (
    onValid: SubmitHandler<FormData>,
    onInvalid?: SubmitErrorHandler<FormData>
  ) => {
    return handleSubmit(async (data) => {
      const isValidatingNow =
        backendValidation &&
        typeof backendValidation.validate === 'function' &&
        'isValidatingRef' in backendValidationResult
          ? backendValidationResult.isValidatingRef.current
          : isBackendValidating;
      if ((backendValidation && !isBackendValid) || isValidatingNow) {
        return;
      }
      await onValid(data);
    }, onInvalid);
  };

  return {
    control,
    handleSubmit: wrappedHandleSubmit,
    errors: formErrors,
    backendFormattedErrors,
    frontendFormattedErrors,
    formattedErrors,
    warnings,
    handleErrorClick,
    handleBlur,
    isDirty,
    defaultValues: formDefaultValues,
    isValid: isFormValid,
    isValidating: isBackendValidating,
    isBackendValidationCurrent,
    canSubmit,
    getValues,
    trigger: triggerWrapper,
    watch,
    setError,
    clearErrors,
    triggerField,
    setValue,
    reset,
    subscribe,
  };
};
